// Talks to the SALES backend routes/feedback.js, which proxies the RNL feedback
// API and holds the X-API-Key server-side.
//
// The vendor is NOT callable from the browser directly: their CORS preflight
// omits X-API-Key from Access-Control-Allow-Headers and their responses carry no
// Access-Control-Allow-Origin. Point this at sandboxrnl directly and every
// request fails as an opaque CORS error, so keep it on our own backend.
import axios from "axios";

// Same shape as CpSourcingApis.js — REACT_APP_PAYMENT_LINK_URL already ends in
// /api/, and the sandbox reverse proxy strips one /api before the backend sees it.
const BASE = `${process.env.REACT_APP_PAYMENT_LINK_URL}api/feedback`;

// Vendor cap. Mirrored here only to keep the UI from asking for more.
export const MAX_PAGE_SIZE = 200;

export const FEEDBACK_STATUS = { PENDING: "PENDING", SUBMITTED: "SUBMITTED" };

// Only sent_at / requested_at / submitted_at are sortable upstream.
export const FEEDBACK_SORT_FIELDS = ["sent_at", "requested_at", "submitted_at"];

/**
 * Every feedback form Sales App has sent — including ones the customer has not
 * answered yet. `page` is 1-based (the vendor's convention), while MUI DataGrid
 * is 0-based; the caller converts.
 *
 * @returns {Promise<{page:number, page_size:number, total:number, results:Array}>}
 */
export const getSentFeedback = async (params = {}) => {
  const res = await axios.get(`${BASE}/sent`, { params });
  return res.data; // { success, page, page_size, total, results }
};

/**
 * Answers for one feedback record, fetched only when the details view opens so
 * the list never carries answer payloads for every row.
 *
 * There is no per-record endpoint (GET /sent/:id is a 404) and feedback_id is
 * not a supported filter — it is silently ignored upstream, which would return
 * the whole list. source_reference_id is the only usable handle, and it is NOT
 * unique, so the match on feedback_id here is what makes the result correct.
 */
export const getFeedbackAnswers = async (row) => {
  if (!row?.source_reference_id) return null;
  const res = await axios.get(`${BASE}/sent`, {
    params: {
      source_reference_id: row.source_reference_id,
      include: "answers",
      page_size: MAX_PAGE_SIZE,
    },
  });
  const results = res.data?.results || [];
  const match = results.find((r) => r.feedback_id === row.feedback_id);
  return match?.answers || [];
};

/**
 * A resend needs stage_code + source_reference_id; without both the vendor
 * cannot identify the request. Exported so the UI can disable the button rather
 * than offer an action that is guaranteed to fail.
 */
export const canResendFeedback = (row) =>
  Boolean(row?.stage_code && row?.source_reference_id);

/**
 * Re-delivers the feedback form link for an existing request.
 *
 * WARNING: on success this puts a real WhatsApp message and email in front of a
 * real customer. There is no dry-run mode, so callers must confirm with the user
 * first.
 *
 * stage_code + source_reference_id identify the existing request — resend never
 * creates one, and an unknown pair comes back as 404 "No feedback request found
 * to re-send". The contact fields are optional and are forwarded so the vendor
 * delivers to the details currently on the record.
 *
 * sentBy is the logged-in user’s name. The vendor ignores it; it exists so the
 * backend can name who re-sent the link on the CRM timeline entry.
 */
export const resendFeedbackLink = async (row, sentBy) => {
  const res = await axios.post(`${BASE}/resend`, {
    stage_code: row?.stage_code,
    source_reference_id: row?.source_reference_id,
    customer_name: row?.customer_name,
    mobile: row?.mobile,
    email: row?.email,
    sent_by: sentBy,
  });
  return res.data; // { success, ...vendor response }
};
